import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import * as moment from 'moment-timezone';
import _ from 'lodash';

import Button from 'shared-components/Button/Button';
import PastDue from 'shared-components/PastDue/PastDue';
import { getDeviceType } from 'utils/device';
import {
  track,
  segmentEnum,
  REACTIVATE_TRACKING_DATA,
  EXPERIENCES,
  trackUpgradeStarted,
  trackReactivateStarted,
} from 'utils/segment';
import env from 'utils/env';

import productBg from '../../images/closeAddOns/product_bg.png';
import desktopBg from '../../images/closeAddOns/bg2.jpg';
import circleBg from '../../images/closeAddOns/circle.png';
import mobileBg from '../../images/closeAddOns/mobile-bg2.jpg';
import membershipImg from '../../images/closeAddOns/membership.jpg';

import { PRODUCTS_COPY, MEMBERSHIP_COPY, BUTTON } from './constants';

const PageRow = styled.div.attrs(() => ({
  className: 'page-row',
}))``;
const Hero = styled.div`
  align-items: center;
  background-image: url(${mobileBg});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  display: flex;
  flex-direction: column;
  padding: 0 12px;
  text-align: center;
  width: 100%;
  height: 400px;
  justify-content: center;

  button {
    width: 100%;
  }

  @media (min-width: 768px) {
    background-image: url(${desktopBg});

    button {
      width: 230px;
    }
  }
`;
const H1Hero = styled.div`
  font-size: 36px;
  font-family: 'PlayFair Display';
  font-weight: 700;
  line-height: 42px;
  margin-bottom: 6px;
  text-align: center;
  max-width: 350px;

  &.ended {
    max-width: 384px;
    padding: 0;
  }

  @media (min-width: 768px) {
    font-size: 42px;
    line-height: 48px;
    margin-bottom: 12px;
    max-width: 450px;
  }
`;
const H1 = styled.div`
  font-size: 30px;
  font-family: 'PlayFair Display';
  font-weight: 700;
  margin-bottom: 6px;
  text-align: center;

  @media (min-width: 768px) {
    font-size: 36px;
    margin-bottom: 12px;
  }
`;
const H2 = styled.div`
  font-size: 16px;
  margin-bottom: 18px;
  text-align: center;
  font-family: 'Nunito Sans';
  line-height: 24px;

  @media (min-width: 768px) {
    font-size: 18px;
    max-width: 588px;
    margin: 0 auto 30px auto;

    &.select {
      max-width: none;
    }
  }
`;
const H2Hero = styled(H2)`
  max-width: 210px;

  a {
    color: #fb6a66;
  }

  @media (min-width: 768px) {
    max-width: 364px;
  }
`;
const H2HeroBold = styled(H2Hero)`
  font-weight: 700;
  margin-bottom: 12px;

  @media (min-width: 768px) {
    max-width: 100%;
  }
`;
const TileContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 36px;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-around;
  }
`;
const ProductsContainerBg = styled(TileContainer)`
  @media (min-width: 1200px) {
    background-image: url(${productBg});
    background-position: center 0;
    background-repeat: no-repeat;
    background-size: contain;
    margin: 48px 0;
    flex-wrap: wrap;
  }
`;
const H3 = styled.div`
  font-family: 'Nunito Sans';
  font-size: 18px;
  font-weight: 700;
  text-align: center;
`;
const H3Left = styled(H3)`
  text-align: left;
`;
const Tile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 290px;
  margin-bottom: 30px;

  &:last-child {
    margin-bottom: 0;
  }
`;
const Text = styled.p`
  opacity: 0.6;
  font-family: 'Nunito Sans';
  font-size: 14px;
  text-align: center;
  color: #000;
  margin: 6px 0 0;

  @media (min-width: 768px) {
    font-size: 16px;
  }
`;
const Icon = styled.i`
  color: #fa9c98;
  font-size: 48px;
  margin-bottom: 18px;

  @media (min-width: 768px) {
    font-size: 65px;
  }
`;
const ProductTile = styled(Tile)`
  margin-bottom: 36px;
`;
const ProductImage = styled.img`
  height: 200px;
  margin-bottom: 12px;

  @media (min-width: 768px) {
    height: 200px;
    margin-bottom: 34px;
  }
`;
const ProductName = styled.div`
  font-size: 16px;
  text-align: center;
  font-family: 'Nunito Sans';
  margin-bottom: 2px;
  white-space: nowrap;
`;
const HR = styled.div`
  margin: 30px auto;
  height: 1px;
  background-image: linear-gradient(
    to left,
    #ffffff,
    #dddddd 19%,
    #d0d0d0 52%,
    #e6e6e6 83%,
    #ffffff
  );
`;
const MembershipContainerBgMobile = styled.div`
  background-image: url(${circleBg});
  background-position: -194px 48px;
  background-repeat: no-repeat;
  background-size: 327px;

  @media (min-width: 768px) {
    background: none;
  }
`;
const MembershipContainerBg = styled.div`
  @media (min-width: 768px) {
    background-image: url(${circleBg});
    background-position: 0 52px;
    background-repeat: no-repeat;
    background-size: 327px;
  }
`;
const MembershipContent = styled.div`
  text-align: center;

  @media (min-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    align-items: flex-start;
  }
`;
const MembershipTileContainer = styled.div`
  @media (min-width: 768px) {
    margin-left: 44px;
  }
`;
const Image2 = styled.img`
  margin-bottom: 25px;
  max-width: 100%;

  @media (min-width: 768px) {
    width: 500px;
    margin-bottom: 50px;
  }
`;
const Icon2Container = styled.div`
  width: 50px;
`;
const Icon2 = styled.i`
  color: #fa9c98;
  font-size: 39px;
  width: 50px;
`;
const TileMembership = styled.div`
  display: flex;
  margin-bottom: 24px;
`;
const TileRight = styled.div`
  margin-left: 18px;

  @media (min-width: 768px) {
    max-width: 250px;
  }
`;
const TileText = styled(Text)`
  font-size: 16px;
  text-align: left;
  margin: 0;
`;

export default function ClosedSale(props) {
  const {
    campaign: {
      campaignId,
      campaignName,
      premiumStartDate,
      premiumEndDate,
      startDate,
      endDate,
    },
    user: { subscription, isAccountPastDue, userId },
  } = props;

  let {
    campaign: { campaignStatus },
    user: { role, subscriptionState },
  } = props;

  campaignStatus = _.snakeCase(campaignStatus);
  subscriptionState = _.snakeCase(subscriptionState);
  role = _.lowerCase(role);
  const isExpired = subscriptionState === 'expired';
  const isExpiredOrCancelledWithZeroBoxes =
    isExpired || subscriptionState === 'cancelled_with_zero_boxes';

  const trackClick = (e) => {
    track(_.get(segmentEnum, 'clickedElement'), {
      user_id: userId,
      role,
      subscription_state: subscriptionState,
      campaign_id: campaignId,
      campaign_name: campaignName,
      experience: 'Closed Page CTA',
      element_name: _.get(e, 'target.innerText'),
      device: getDeviceType(),
    });
  };

  const handleClickUpgrade = (redirectLink) => {
    trackUpgradeStarted({
      cta: BUTTON.upgrade.name,
      experience: EXPERIENCES.addOns,
    }).finally(() => {
      window.location.href = redirectLink;
    });
  };

  const handleClickReactivate = (redirectLink) => {
    const {
      expiredType,
      canceledType,
      expiredReactivationType,
      canceledReactivationType,
    } = REACTIVATE_TRACKING_DATA;
    const type = isExpired ? expiredType : canceledType;
    const reactivationType = isExpired
      ? expiredReactivationType
      : `${canceledReactivationType} ${subscription}`;
    trackReactivateStarted({
      cta: BUTTON.reactivate.name,
      type,
      experience: EXPERIENCES.addOns,
      reactivationType,
    }).finally(() => {
      window.location.href = redirectLink;
    });
  };

  const handleClickCTA = (e, type, link) => {
    const redirectLink = link?.includes('fabfitfun.com')
      ? link
      : `https://${env.hostname}/${link}`;
    switch (type) {
      case 'upgrade': {
        handleClickUpgrade(redirectLink);
        break;
      }
      case 'reactivate': {
        handleClickReactivate(redirectLink);
        break;
      }
      default: {
        trackClick(e);
        window.location.href = redirectLink;
      }
    }
  };

  const renderCTA = (type, h2) => {
    const { name, link } = BUTTON[type];

    return (
      <>
        {h2 && <H2HeroBold>{h2}</H2HeroBold>}
        <Button
          onClick={(e) => {
            handleClickCTA(e, type, link);
          }}
        >
          {name}
        </Button>
      </>
    );
  };

  const formatDate = (date) => {
    return moment(date).tz('America/Los_Angeles').format('MMMM D [at] h:mm a');
  };

  const formatBillDate = (date) => {
    return moment(date)
      .add(2, 'days')
      .tz('America/Los_Angeles')
      .format('MMMM D');
  };

  const formatNewMembersDate = (date) => {
    return moment(date)
      .add(1, 'days')
      .tz('America/Los_Angeles')
      .format('MMMM D');
  };

  /*
   * copy block is organized as:
   * {user role (select, customer, trial, ...)-> campaign status (before_start, window_one, ...) -> section of page (hero, howAddOnsWorks...) -> content}
   * exceptions are for expired and cancelled_with_zero_boxes which are subscription states but are organized at the same level as user role since they have dedicated UIs
   * collapse different top level fields to make this object more managable while developing
   */
  const copy = {
    select: {
      before_start: {
        hero: {
          header: 'Add-Ons Opens Soon',
          subHeader: `Your early access starts on ${formatDate(
            premiumStartDate
          )} PT.`,
          CTA: renderCTA('calendar'),
        },
        howAddOnsWorks: {
          subHeader: `If you’re a new Annual Member who signed up on or before ${formatNewMembersDate(
            premiumEndDate
          )}, your items will ship FREE with your seasonal box!`,
          shopTheSale: `Add items to your cart up until the sale closes on ${formatDate(
            premiumEndDate
          )} PT.`,
          noCheckoutRequired: `Everything in your cart will be automatically billed on ${formatBillDate(
            premiumEndDate
          )}.`,
        },
        upgrade: {
          header: 'Get the Most Out of Your Membership',
          subheader:
            'As an Annual Member, you get additional perks. Make sure to take advantage of all of them!',
        },
      },
      mid: {
        hero: {
          header: 'Add-Ons Opens Soon',
          subHeader: `Start shopping on ${formatDate(startDate)} PT.`,
          CTA: renderCTA('calendar'),
        },
        howAddOnsWorks: {
          subHeader: `Get up to 70% off beauty, fashion, and wellness products. If you’re a new Annual Member who signed up on or before ${formatNewMembersDate(
            premiumEndDate
          )}, your items will ship FREE with your seasonal box!`,
          shopTheSale: `Add items to your cart up until the sale closes on ${formatDate(
            endDate
          )} PT.`,
          noCheckoutRequired: `Everything in your cart will be automatically billed on ${formatBillDate(
            endDate
          )}.`,
        },
        upgrade: {
          header: 'Get the Most Out of Your Membership',
          subheader:
            'As an Annual Member, you get additional perks. Make sure to take advantage of all of them!',
        },
      },
      ended: {
        hero: {
          header: 'Add-Ons is Closed for the Season',
          subHeader:
            'In the meantime, check out our amazing products in the Shop!',
          CTA: renderCTA('shop'),
        },
        howAddOnsWorks: {
          shopTheSale: 'Add items to your cart up until the sale closes.',
          noCheckoutRequired:
            'Everything in your cart will be automatically billed after the sale closes.',
        },
        upgrade: {
          header: 'Get the Most Out of Your Membership',
          subheader:
            'As an Annual Member, you get additional perks. Make sure to take advantage of all of them!',
        },
      },
    },
    customer: {
      before_start: {
        hero: {
          header: 'Add-Ons Opens Soon',
          subHeader: `Start shopping on ${formatDate(startDate)} PT.`,
          CTA: renderCTA(
            'upgrade',
            'Want early access to sales and Customization?'
          ),
        },
        howAddOnsWorks: {
          shopTheSale: `Add items to your cart up until the sale closes on ${formatDate(
            endDate
          )} PT.`,
          noCheckoutRequired: `Everything in your cart will be automatically billed on ${formatBillDate(
            endDate
          )}.`,
        },
        upgrade: {
          header: 'Why Upgrade?',
          subheader: 'Discover the perks of being an Annual Member!',
          CTA: renderCTA('upgrade'),
        },
      },
      window_one: {
        hero: {
          header: 'Add-Ons is Open for Annual Members',
          subHeader: `Start shopping on ${formatDate(startDate)} PT.`,
          CTA: renderCTA(
            'upgrade',
            'Want early access to sales and Customization?'
          ),
        },
        howAddOnsWorks: {
          shopTheSale: `Add items to your cart up until the sale closes on ${formatDate(
            endDate
          )} PT.`,
          noCheckoutRequired: `Everything in your cart will be automatically billed on ${formatBillDate(
            endDate
          )}.`,
        },
        upgrade: {
          header: 'Why Upgrade?',
          subheader: 'Discover the perks of being an Annual Member!',
          CTA: renderCTA('upgrade'),
        },
      },
      mid: {
        hero: {
          header: 'Add-Ons Opens Soon',
          subHeader: `Start shopping on ${formatDate(startDate)} PT.`,
          CTA: renderCTA('calendar'),
        },
        howAddOnsWorks: {
          shopTheSale: `Add items to your cart up until the sale closes on ${formatDate(
            endDate
          )} PT.`,
          noCheckoutRequired: `Everything in your cart will be automatically billed on ${formatBillDate(
            endDate
          )}.`,
        },
        upgrade: {
          header: 'Why Upgrade?',
          subheader: 'Discover the perks of being an Annual Member!',
          CTA: renderCTA('upgrade'),
        },
      },
      ended: {
        hero: {
          header: 'Add-Ons is Closed for the Season',
          subHeader:
            'In the meantime, check out our amazing products in the Shop!',
          CTA: renderCTA(
            'upgrade',
            'Want early access to sales and Customization?'
          ),
        },
        howAddOnsWorks: {
          shopTheSale: 'Add items to your cart up until the sale closes.',
          noCheckoutRequired:
            'Everything in your cart will be automatically billed after the sale closes.',
        },
        upgrade: {
          header: 'Why Upgrade?',
          subheader: 'Discover the perks of being an Annual Member!',
          CTA: renderCTA('upgrade'),
        },
      },
    },
    trial: {
      before_start: {
        hero: {
          header: 'Add-Ons Opens Soon',
          subHeader: `Start shopping on ${formatDate(startDate)} PT.`,
          CTA: renderCTA(
            'upgrade',
            'Want early access to sales and Customization?'
          ),
        },
        howAddOnsWorks: {
          shopTheSale: `Add items to your cart up until the sale closes on ${formatDate(
            endDate
          )} PT.`,
          noCheckoutRequired: `Everything in your cart will be automatically billed on ${formatBillDate(
            endDate
          )}.`,
        },
        upgrade: {
          header: 'Why Upgrade?',
          subheader: 'Discover the perks of being an Annual Member!',
          CTA: renderCTA('upgrade'),
        },
      },
      window_one: {
        hero: {
          header: 'Add-Ons is Open for Annual Members',
          subHeader: `Start shopping on ${formatDate(startDate)} PT.`,
          CTA: renderCTA(
            'upgrade',
            'Want early access to sales and Customization?'
          ),
        },
        howAddOnsWorks: {
          shopTheSale: `Add items to your cart up until the sale closes on ${formatDate(
            endDate
          )} PT.`,
          noCheckoutRequired: `Everything in your cart will be automatically billed on ${formatBillDate(
            endDate
          )}.`,
        },
        upgrade: {
          header: 'Why Upgrade?',
          subheader: 'Discover the perks of being an Annual Member!',
          CTA: renderCTA('upgrade'),
        },
      },
      mid: {
        hero: {
          header: 'Add-Ons Opens Soon',
          subHeader: `Start shopping on ${formatDate(startDate)} PT.`,
          CTA: renderCTA(
            'calendar',
            'Want early access to sales and Customization?'
          ),
        },
        howAddOnsWorks: {
          shopTheSale: `Add items to your cart up until the sale closes on ${formatDate(
            endDate
          )} PT.`,
          noCheckoutRequired: `Everything in your cart will be automatically billed on ${formatBillDate(
            endDate
          )}.`,
        },
        upgrade: {
          header: 'Why Upgrade?',
          subheader: 'Discover the perks of being an Annual Member!',
          CTA: renderCTA('upgrade'),
        },
      },
      ended: {
        hero: {
          header: 'Add-Ons is Closed for the Season',
          subHeader:
            'In the meantime, check out our amazing products in the Shop!',
          CTA: renderCTA('upgrade', 'Want to shop the next sale?'),
        },
        howAddOnsWorks: {
          shopTheSale: 'Add items to your cart up until the sale closes.',
          noCheckoutRequired:
            'Everything in your cart will be automatically billed after the sale closes.',
        },
        upgrade: {
          header: 'Why Upgrade?',
          subheader: 'Discover the perks of being an Annual Member!',
          CTA: renderCTA('upgrade'),
        },
      },
    },
    expired: {
      before_start: {
        hero: {
          header: 'Add-Ons Opens Soon',
          subHeader: `Start shopping on ${formatDate(premiumStartDate)} PT.`,
          CTA: renderCTA('reactivate', 'Want to shop Add-Ons?'),
        },
        howAddOnsWorks: {
          shopTheSale: `Add items to your cart up until the sale closes on ${formatDate(
            premiumEndDate
          )} PT.`,
          noCheckoutRequired: `Everything in your cart will be automatically billed on ${formatBillDate(
            premiumEndDate
          )}.`,
        },
        upgrade: {
          header: 'Why Reactivate?',
          subheader: 'Discover the perks of being a Member!',
          CTA: renderCTA('reactivate'),
        },
      },
      window_one: {
        hero: {
          header: 'Add-Ons is Open for Annual Members',
          subHeader: '',
          CTA: renderCTA('reactivate', 'Want to shop Add-Ons?'),
        },
        howAddOnsWorks: {
          shopTheSale: `Add items to your cart up until the sale closes on ${formatDate(
            endDate
          )} PT.`,
          noCheckoutRequired: `Everything in your cart will be automatically billed on ${formatBillDate(
            endDate
          )}.`,
        },
        upgrade: {
          header: 'Why Reactivate?',
          subheader: 'Discover the perks of being a Member!',
          CTA: renderCTA('reactivate'),
        },
      },
      mid: {
        hero: {
          header: 'Add-Ons Opens Soon',
          subHeader: `Start shopping on ${formatDate(startDate)} PT.`,
          CTA: renderCTA('reactivate', 'Want to shop Add-Ons?'),
        },
        howAddOnsWorks: {
          shopTheSale: `Add items to your cart up until the sale closes on ${formatDate(
            endDate
          )} PT.`,
          noCheckoutRequired: `Everything in your cart will be automatically billed on ${formatBillDate(
            endDate
          )}.`,
        },
        upgrade: {
          header: 'Why Reactivate?',
          subheader: 'Discover the perks of being a Member!',
          CTA: renderCTA('reactivate'),
        },
      },
      window_two: {
        hero: {
          header: 'Add-Ons is Now Open',
          subHeader: '',
          CTA: renderCTA('reactivate', 'Want to shop Add-Ons?'),
        },
        howAddOnsWorks: {
          shopTheSale: `Add items to your cart up until the sale closes on ${formatDate(
            endDate
          )} PT.`,
          noCheckoutRequired: `Everything in your cart will be automatically billed on ${formatBillDate(
            endDate
          )}.`,
        },
        upgrade: {
          header: 'Why Reactivate?',
          subheader: 'Discover the perks of being a Member!',
          CTA: renderCTA('reactivate'),
        },
      },
      ended: {
        hero: {
          header: 'Add-Ons is Closed for the Season',
          subHeader: '',
          CTA: renderCTA('reactivate', 'Want to shop the next sale?'),
        },
        howAddOnsWorks: {
          shopTheSale: 'Add items to your cart up until the sale closes.',
          noCheckoutRequired:
            'Everything in your cart will be automatically billed after the sale closes.',
        },
        upgrade: {
          header: 'Why Reactivate?',
          subheader: 'Discover the perks of being a Member!',
          CTA: renderCTA('reactivate'),
        },
      },
    },
    club: {
      before_start: {
        hero: {
          header: 'Add-Ons Will Open to Members Soon',
          subHeader: `Start shopping on ${formatDate(premiumStartDate)} PT.`,
          CTA: renderCTA(
            'activate',
            'Want early access to sales and Customization?'
          ),
        },
        howAddOnsWorks: {
          shopTheSale: `Add items to your cart up until the sale closes on ${formatDate(
            premiumEndDate
          )} PT.`,
          noCheckoutRequired: `Everything in your cart will be automatically billed on ${formatBillDate(
            premiumEndDate
          )}.`,
        },
        upgrade: {
          header: 'Why Activate?',
          subheader: 'Discover the perks of being a Member!',
          CTA: renderCTA('activate'),
        },
      },
      window_one: {
        hero: {
          header: 'Add-Ons is Open for Annual Members',
          subHeader: `Start shopping on ${formatDate(premiumStartDate)} PT.`,
          CTA: renderCTA(
            'activate',
            'Want early access to sales and Customization?'
          ),
        },
        howAddOnsWorks: {
          shopTheSale: `Add items to your cart up until the sale closes on ${formatDate(
            endDate
          )} PT.`,
          noCheckoutRequired: `Everything in your cart will be automatically billed on ${formatBillDate(
            endDate
          )}.`,
        },
        upgrade: {
          header: 'Why Activate?',
          subheader: 'Discover the perks of being a Member!',
          CTA: renderCTA('activate'),
        },
      },
      mid: {
        hero: {
          header: 'Add-Ons Opens Soon',
          subHeader: `Start shopping on ${formatDate(startDate)} PT.`,
          CTA: renderCTA('activate', 'Want to shop Add-Ons?'),
        },
        howAddOnsWorks: {
          shopTheSale: `Add items to your cart up until the sale closes on ${formatDate(
            endDate
          )} PT.`,
          noCheckoutRequired: `Everything in your cart will be automatically billed on ${formatBillDate(
            endDate
          )}.`,
        },
        upgrade: {
          header: 'Why Activate?',
          subheader: 'Discover the perks of being a Member!',
          CTA: renderCTA('activate'),
        },
        memberPerks: {
          list: MEMBERSHIP_COPY.club,
        },
      },
      window_two: {
        hero: {
          header: 'Add-Ons is Open for Members Only',
          subHeader: `This sale closes on ${formatDate(endDate)} PT.`,
          CTA: renderCTA('activate', 'Want to shop Add-Ons?'),
        },
        howAddOnsWorks: {
          shopTheSale: `Add items to your cart up until the sale closes on ${formatDate(
            endDate
          )} PT.`,
          noCheckoutRequired: `Everything in your cart will be automatically billed on ${formatBillDate(
            endDate
          )}.`,
        },
        upgrade: {
          header: 'Why Activate?',
          subheader: 'Discover the perks of being a Member!',
          CTA: renderCTA('activate'),
        },
        memberPerks: {
          list: MEMBERSHIP_COPY.club,
        },
      },
      ended: {
        hero: {
          header: 'Add-Ons is Closed for the Season',
          subHeader:
            'In the meantime, check out our amazing products in the <a href="/shop">Shop</a>!',
          CTA: renderCTA('activate', 'Want to shop the next sale?'),
        },
        howAddOnsWorks: {
          shopTheSale: 'Add items to your cart up until the sale closes.',
          noCheckoutRequired:
            'Everything in your cart will be automatically billed after the sale closes.',
        },
        upgrade: {
          header: 'Why Activate?',
          subheader: 'Discover the perks of being a Member!',
          CTA: renderCTA('activate'),
        },
        memberPerks: {
          list: MEMBERSHIP_COPY.club,
        },
      },
    },
    cancelled_with_zero_boxes: {
      before_start: {
        hero: {
          header: 'Add-Ons Opens Soon',
          subHeader: `Start shopping on ${formatDate(premiumStartDate)} PT.`,
          CTA: renderCTA('reactivate', 'Want to shop Add-Ons?'),
        },
        howAddOnsWorks: {
          shopTheSale: `Add items to your cart up until the sale closes on ${formatDate(
            premiumEndDate
          )} PT.`,
          noCheckoutRequired: `Everything in your cart will be automatically billed on ${formatBillDate(
            premiumEndDate
          )}.`,
        },
        upgrade: {
          header: 'Why Reactivate?',
          subheader: 'Discover the perks of being a Member!',
          CTA: renderCTA('reactivate'),
        },
      },
      window_one: {
        hero: {
          header: 'Add-Ons is Open for Annual Members',
          subHeader: '',
          CTA: renderCTA('reactivate', 'Want to shop Add-Ons?'),
        },
        howAddOnsWorks: {
          shopTheSale: `Add items to your cart up until the sale closes on ${formatDate(
            endDate
          )} PT.`,
          noCheckoutRequired: `Everything in your cart will be automatically billed on ${formatBillDate(
            endDate
          )}.`,
        },
        upgrade: {
          header: 'Why Reactivate?',
          subheader: 'Discover the perks of being a Member!',
          CTA: renderCTA('reactivate'),
        },
      },
      mid: {
        hero: {
          header: 'Add-Ons Opens Soon',
          subHeader: `Start shopping on ${formatDate(startDate)} PT.`,
          CTA: renderCTA('reactivate', 'Want to shop Add-Ons?'),
        },
        howAddOnsWorks: {
          shopTheSale: `Add items to your cart up until the sale closes on ${formatDate(
            endDate
          )} PT.`,
          noCheckoutRequired: `Everything in your cart will be automatically billed on ${formatBillDate(
            endDate
          )}.`,
        },
        upgrade: {
          header: 'Why Reactivate?',
          subheader: 'Discover the perks of being a Member!',
          CTA: renderCTA('reactivate'),
        },
      },
      window_two: {
        hero: {
          header: 'Add-Ons is Now Open',
          subHeader: '',
          CTA: renderCTA('reactivate', 'Want to shop Add-Ons?'),
        },
        howAddOnsWorks: {
          shopTheSale: `Add items to your cart up until the sale closes on ${formatDate(
            endDate
          )} PT.`,
          noCheckoutRequired: `Everything in your cart will be automatically billed on ${formatBillDate(
            endDate
          )}.`,
        },
        upgrade: {
          header: 'Why Reactivate?',
          subheader: 'Discover the perks of being a Member!',
          CTA: renderCTA('reactivate'),
        },
      },
      ended: {
        hero: {
          header: 'Add-Ons is Closed for the Season',
          subHeader: '',
          CTA: renderCTA('reactivate', 'Want to get access when it does open?'),
        },
        howAddOnsWorks: {
          shopTheSale: 'Add items to your cart up until the sale closes.',
          noCheckoutRequired:
            'Everything in your cart will be automatically billed after the sale closes.',
        },
        upgrade: {
          header: 'Why Reactivate?',
          subheader: 'Discover the perks of being a Member!',
          CTA: renderCTA('reactivate'),
        },
      },
    },
  };

  const getRole = () =>
    isExpiredOrCancelledWithZeroBoxes ? subscriptionState : role;

  const getCopy = (section, element, defaultValue = '') =>
    _.get(
      copy,
      `${getRole()}.${campaignStatus}.${section}.${element}`,
      defaultValue
    );

  const memberPerks = getCopy('memberPerks', 'list', MEMBERSHIP_COPY.default);

  return (
    <>
      <Hero>
        <H1Hero className={campaignStatus}>{getCopy('hero', 'header')}</H1Hero>
        {getCopy('hero', 'subHeader') && (
          <H2Hero>
            <div
              dangerouslySetInnerHTML={{ __html: getCopy('hero', 'subHeader') }}
            />
          </H2Hero>
        )}
        {getCopy('hero', 'CTA')}
      </Hero>
      <PageRow>
        <H1 style={{ marginTop: 40 }}>How Add-Ons Works</H1>
        <H2>
          {getCopy('howAddOnsWorks', 'subHeader') ||
            'Get up to 70% off beauty, fashion, fitness, and wellness products that ship for free with your box.'}
        </H2>
        <TileContainer>
          <Tile>
            <Icon className="fal fa-shopping-cart" />
            <H3>Shop the Sale</H3>
            <Text>{getCopy('howAddOnsWorks', 'shopTheSale')}</Text>
          </Tile>
          <Tile>
            <Icon className="fal fa-box-full" />
            <H3>No Checkout Required</H3>
            <Text>{getCopy('howAddOnsWorks', 'noCheckoutRequired')}</Text>
          </Tile>
          <Tile>
            <Icon className="fal fa-smile-beam" />
            <H3>Get Excited</H3>
            <Text>
              Your items will generally ship within 30 days of your bill date.
              Check your inbox for updates.
            </Text>
          </Tile>
        </TileContainer>
        <HR />
        <H1>Past Add-Ons Favorites</H1>
        <ProductsContainerBg>
          {PRODUCTS_COPY.map((product) => (
            <ProductTile key={product.name}>
              <ProductImage src={product.image} />
              <H3>{product.title}</H3>
              <ProductName>{product.name}</ProductName>
            </ProductTile>
          ))}
        </ProductsContainerBg>
        <HR />
      </PageRow>
      <MembershipContainerBgMobile>
        <PageRow style={{ marginBottom: 30 }}>
          <MembershipContainerBg>
            <H1>{getCopy('upgrade', 'header')}</H1>
            <H2 className={getRole()} style={{ fontSize: 16 }}>
              {getCopy('upgrade', 'subheader')}
            </H2>
            <MembershipContent>
              <Image2 src={membershipImg} />
              <MembershipTileContainer>
                {memberPerks.map((item) => (
                  <TileMembership key={item.title}>
                    <Icon2Container>
                      <Icon2 className={item.icon} />
                    </Icon2Container>
                    <TileRight>
                      <H3Left>{item.title}</H3Left>
                      <TileText>{item.description}</TileText>
                    </TileRight>
                  </TileMembership>
                ))}
                {getCopy('upgrade', 'CTA')}
              </MembershipTileContainer>
            </MembershipContent>
          </MembershipContainerBg>
        </PageRow>
      </MembershipContainerBgMobile>
      <PastDue isAccountPastDue={isAccountPastDue} />
    </>
  );
}

ClosedSale.propTypes = {
  campaign: PropTypes.shape({
    campaignId: PropTypes.number.isRequired,
    campaignName: PropTypes.string.isRequired,
    premiumStartDate: PropTypes.string.isRequired,
    premiumEndDate: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    campaignStatus: PropTypes.string.isRequired,
  }).isRequired,
  user: PropTypes.shape({
    subscription: PropTypes.string.isRequired,
    isAccountPastDue: PropTypes.bool.isRequired,
    userId: PropTypes.number.isRequired,
    role: PropTypes.string.isRequired,
    subscriptionState: PropTypes.string.isRequired,
  }).isRequired,
};
